export interface SearchPattern {
  pattern: RegExp;
  type: 'area' | 'pincode';
  priority: number;
}

export const searchPatterns: SearchPattern[] = [
  // Pincode patterns
  { pattern: /^\d{6}$/, type: 'pincode', priority: 1 },
  { pattern: /pincode\s+(\d{6})/i, type: 'pincode', priority: 1 },
  { pattern: /(\d{6})/i, type: 'pincode', priority: 1 },
  
  // Question patterns for area
  { pattern: /what\s+is\s+(?:the\s+)?pincode\s+(?:of\s+)?(.+?)(?:\s+(?:in|at|near))?\??$/i, type: 'area', priority: 2 },
  { pattern: /pincode\s+(?:of\s+)?(.+?)(?:\s+(?:in|at|near))?\??$/i, type: 'area', priority: 2 },
  { pattern: /(.+?)\s+pincode\??$/i, type: 'area', priority: 2 },
  
  // Location patterns
  { pattern: /(.+?)\s+(?:area|locality|region|place)\s+pincode/i, type: 'area', priority: 3 },
  { pattern: /(.+?)\s+(?:district|city|town|village)\s+pincode/i, type: 'area', priority: 3 },
  
  // State/City specific patterns
  { pattern: /(.+?)\s+(?:in|at|near)\s+(.+?)\s+pincode/i, type: 'area', priority: 4 },
  { pattern: /(.+?),\s*(.+?)\s+pincode/i, type: 'area', priority: 4 },
  
  // Direct area name (lowest priority, but most flexible)
  { pattern: /^(.+)$/i, type: 'area', priority: 5 },
];

export interface ExtractedQuery {
  type: 'area' | 'pincode';
  value: string;
  state?: string;
  originalQuery: string;
}

export function extractSearchQuery(query: string): ExtractedQuery {
  // Clean and normalize the query
  const normalizedQuery = query.trim()
    .toLowerCase()
    .replace(/[^\w\s,-]/g, '') // Remove special characters except comma and hyphen
    .replace(/\s+/g, ' '); // Normalize spaces
  
  // First check for pincode pattern
  const pincodeMatch = normalizedQuery.match(/\d{6}/);
  if (pincodeMatch) {
    return {
      type: 'pincode',
      value: pincodeMatch[0],
      originalQuery: query
    };
  }
  
  // Try each pattern in order of priority
  for (const { pattern, type } of searchPatterns) {
    const match = normalizedQuery.match(pattern);
    if (match) {
      if (type === 'pincode') {
        return {
          type: 'pincode',
          value: match[1] || match[0],
          originalQuery: query
        };
      } else {
        // For area type, check if there's a state/city mentioned
        const areaName = match[1].trim();
        const state = match[2]?.trim();
        
        // Clean the area name
        const cleanAreaName = areaName
          .replace(/\s*pincode\s*/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        
        return {
          type: 'area',
          value: cleanAreaName,
          state,
          originalQuery: query
        };
      }
    }
  }
  
  // If no pattern matches, treat as area name
  // Clean the query from common words
  const cleanQuery = normalizedQuery
    .replace(/\s*pincode\s*/gi, '')
    .replace(/\s*post\s*office\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  
  return {
    type: 'area',
    value: cleanQuery,
    originalQuery: query
  };
}

export function generateSeoTitle(query: ExtractedQuery): string {
  if (query.type === 'pincode') {
    return `${query.value} PIN Code - Post Office Details & Location`;
  }
  
  const areaName = query.value.replace(/\b\w/g, (c) => c.toUpperCase());
  if (query.state) {
    const stateName = query.state.replace(/\b\w/g, (c) => c.toUpperCase());
    return `${areaName} ${stateName} Pincode - Complete Postal Information`;
  }
  
  return `${areaName} Pincode - Find Postal Code & Post Office Details`;
}

export function generateSeoDescription(query: ExtractedQuery, pincode?: string): string {
  const areaName = query.value.replace(/\b\w/g, (c) => c.toUpperCase());
  
  if (query.type === 'pincode') {
    return `Find complete postal information for PIN code ${query.value}. Get post office details, branch type, and delivery status for this area.`;
  }
  
  if (pincode) {
    return `${areaName} pincode is ${pincode}. Get detailed information about post offices, branch types, and delivery status in ${areaName}${query.state ? `, ${query.state}` : ''}.`;
  }
  
  return `Looking for ${areaName} pincode? Find the complete postal code information, post office details, and delivery status for ${areaName}${query.state ? ` in ${query.state}` : ''}.`;
}

export function generateSeoUrl(query: ExtractedQuery): string {
  const cleanValue = query.value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  
  if (query.type === 'pincode') {
    return `/pincode/${query.value}`;
  }
  
  if (query.state) {
    const cleanState = query.state
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    return `/${cleanValue}-${cleanState}-pincode`;
  }
  
  return `/${cleanValue}-pincode`;
}
